import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ResponsiveImage from "../shared/ResponsiveImage.jsx";

function Thumb({ image, height }) {
  return (
    <ResponsiveImage
      src={image.src}
      alt={image.alt}
      variant="light"
      style={{ width: "100%", height, objectFit: "cover", display: "block" }}
    />
  );
}

function LightboxImage({ image }) {
  return (
    <ResponsiveImage
      src={image.src}
      alt={image.alt}
      priority
      variant="dark"
      style={{ width: "100%", maxHeight: "80vh", aspectRatio: image.src ? undefined : "16 / 10", objectFit: "contain", borderRadius: "12px", display: "block" }}
    />
  );
}

export default function EventGallery({ images }) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const closeButtonRef = useRef(null);
  const lastTriggerRef = useRef(null);

  useEffect(() => {
    if (openIndex === null) return undefined;
    closeButtonRef.current?.focus();

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpenIndex(null);
      if (event.key === "ArrowRight") setOpenIndex((i) => (i + 1) % images.length);
      if (event.key === "ArrowLeft") setOpenIndex((i) => (i - 1 + images.length) % images.length);
    }
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      lastTriggerRef.current?.focus();
    };
  }, [openIndex, images.length]);

  if (!images?.length) return null;

  const current = openIndex !== null ? images[openIndex] : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-cream)", margin: 0 }}>
        {t("events.gallery")}
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
        {images.map((image, i) => (
          <button
            key={i}
            type="button"
            onClick={(event) => {
              lastTriggerRef.current = event.currentTarget;
              setOpenIndex(i);
            }}
            aria-label={image.alt}
            style={{ border: "none", padding: 0, cursor: "pointer", borderRadius: "10px", overflow: "hidden" }}
          >
            <Thumb image={image} height="140px" />
          </button>
        ))}
      </div>

      {current && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={current.alt}
          onClick={() => setOpenIndex(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,9,7,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
            padding: "40px",
          }}
        >
          <button
            ref={closeButtonRef}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setOpenIndex(null);
            }}
            aria-label={t("events.galleryClose")}
            style={{ position: "absolute", top: "24px", right: "24px", background: "transparent", border: "none", color: "var(--color-cream)", fontSize: "32px", lineHeight: 1, cursor: "pointer" }}
          >
            ×
          </button>

          {images.length > 1 && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setOpenIndex((openIndex - 1 + images.length) % images.length);
              }}
              aria-label={t("events.galleryPrev")}
              style={{ position: "absolute", left: "24px", background: "transparent", border: "none", color: "var(--color-cream)", fontSize: "32px", cursor: "pointer" }}
            >
              ‹
            </button>
          )}

          <div onClick={(event) => event.stopPropagation()} style={{ maxWidth: "min(900px, 90vw)", width: "100%" }}>
            <LightboxImage key={openIndex} image={current} />
            <p style={{ color: "var(--color-cream-soft)", fontSize: "14px", textAlign: "center", marginTop: "12px" }}>{current.alt}</p>
          </div>

          {images.length > 1 && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setOpenIndex((openIndex + 1) % images.length);
              }}
              aria-label={t("events.galleryNext")}
              style={{ position: "absolute", right: "24px", background: "transparent", border: "none", color: "var(--color-cream)", fontSize: "32px", cursor: "pointer" }}
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}
